import React, { useCallback, useEffect, useState } from 'react'
import { X } from 'lucide-react'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
  DrawerFooter,
} from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createTag, updateTag, type CreateTagPayload } from '@/api/tagApi'
import { showErrorToast, showSuccessToast } from '../ui/toast'
import axios from 'axios'
import { TAG_QUERY_KEYS } from '@/api/queryKeys'

interface AddTagDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit?: (tagData: TagFormData) => void
  data?: TagFormData
}

export interface TagFormData {
  id?: string
  name: string
  events?: number
}

const AddTagDrawer: React.FC<AddTagDrawerProps> = ({
  open,
  onOpenChange,
  data,
}) => {
  const [formData, setFormData] = useState<TagFormData>({
    name: '',
  })

  const [errors, setErrors] = useState<Partial<TagFormData>>({})
  const queryClient = useQueryClient()

  const resetForm = useCallback(() => {
    setFormData({
      name: data?.name ?? '',
    })
    setErrors({})
  }, [data])

  useEffect(() => {
    if (open && data?.id) {
      resetForm()
    }
  }, [open, resetForm, data?.id])

  const { mutate: createTagMutation, isPending: creatingTag } = useMutation({
    mutationFn: createTag,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TAG_QUERY_KEYS.all] })

      showSuccessToast(
        'Your new tag has been created.',
        `Tag is now available for event organizers to use.`
      )
      handleReset()
      onOpenChange(false)
    },
    onError: error => {
      if (axios.isAxiosError(error)) {
        const message = error?.response?.data?.message || 'Failed to create Tag'
        showErrorToast('Tag Creation Failed', message)
      }
    },
  })

  const { mutate: updateTagMutation, isPending: updatingTag } = useMutation({
    mutationFn: updateTag,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TAG_QUERY_KEYS.all] })

      showSuccessToast(
        'Your new tag has been updated.',
        `Tag is now available for event organizers to use.`
      )
      handleReset()
      onOpenChange(false)
    },
    onError: error => {
      if (axios.isAxiosError(error)) {
        const message = error?.response?.data?.message || 'Failed to update tag'
        showErrorToast('Tag update Failed', message)
      }
    },
  })

  const handleInputChange = (
    field: keyof TagFormData,
    value: string | boolean
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }))

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Partial<TagFormData> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Tag name is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (validateForm()) {
      const payload: CreateTagPayload = {
        name: formData.name,
      }
      try {
        if (data?.id) {
          const updatePayload = {
            id: data.id,
            payload,
          }
          updateTagMutation(updatePayload)
        } else {
          createTagMutation(payload)
        }
      } catch {
        showErrorToast('Tag Creation Failed', 'Failed to create tag')
      }
    }
  }

  const handleReset = () => {
    setFormData({
      name: '',
    })
    setErrors({})
  }

  const handleClose = () => {
    handleReset()
    onOpenChange(false)
  }

  const isFormValid = formData.name.trim().length > 0

  let buttonText: string
  if (data?.id) {
    buttonText = updatingTag ? 'Saving tag...' : 'Save Tag'
  } else {
    buttonText = creatingTag ? 'Adding tag...' : 'Add Tag'
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent className="w-full sm:w-[400px] h-full bg-ss-neutral-white border-l border-ss-neutral-50 rounded-none lg:rounded-2xl flex flex-col">
        <DrawerHeader className="border-b border-ss-neutral-100 px-4 py-3 sm:px-6">
          <div className="flex items-center justify-between">
            <DrawerTitle className="text-lg font-semibold text-ss-neutral-950">
              {data?.id ? 'Edit Tag' : 'Add Tag'}
            </DrawerTitle>
            <DrawerClose asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={handleClose}
                aria-label="Close drawer"
              >
                <X className="h-4 w-4" />
              </Button>
            </DrawerClose>
          </div>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-6">
            {/* Tag Name Field */}
            <div className="space-y-2">
              <Label
                variant="required"
                htmlFor="tag-name"
                className="text-sm font-medium text-ss-neutral-950"
              >
                Tag Name
              </Label>
              <Input
                id="tag-name"
                value={formData.name}
                onChange={e => handleInputChange('name', e.target.value)}
                placeholder="Enter tag name"
                className={cn(
                  'w-full',
                  errors.name &&
                    'border-ss-critical-200 focus:border-ss-critical-200 focus:ring-ss-critical-200'
                )}
                aria-describedby={errors.name ? 'tag-name-error' : undefined}
              />
              {errors.name && (
                <p
                  id="tag-name-error"
                  className="text-sm text-ss-critical-200"
                  role="alert"
                >
                  {errors.name}
                </p>
              )}
            </div>
          </div>
        </div>

        <DrawerFooter className="border-t border-ss-neutral-100 px-4 py-4 sm:px-6">
          <div className="flex gap-3 sm:justify-end">
            <Button
              variant="outline"
              onClick={handleClose}
              className="flex-1 sm:flex-none"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isFormValid || creatingTag || updatingTag}
              className={cn(
                'flex-1 sm:flex-none',
                isFormValid
                  ? 'bg-ss-blue-900 hover:bg-ss-blue-800 text-white'
                  : 'bg-ss-neutral-200 text-ss-neutral-400 cursor-not-allowed'
              )}
              aria-label="Add new tag"
            >
              {buttonText}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

export default AddTagDrawer
